<template>
  <div class="car-brand-model">
    <!-- 品牌选择 -->
    <div v-if="!onlyModel" class="car-brand-model__brand">
      <el-form-item label="车辆品牌" v-if="showLabel">
        <el-select
          v-model="brandValue"
          :placeholder="brandPlaceholder"
          :disabled="disabled"
          :filterable="true"
          @change="handleBrandChange"
          clearable
          @clear="handleBrandClear"
        >
          <el-option
            v-for="brand in brandList"
            :key="brand.id"
            :label="brand.name"
            :value="brand.id"
          />
        </el-select>
      </el-form-item>
      <el-select
        v-else
        v-model="brandValue"
        :placeholder="brandPlaceholder"
        :disabled="disabled"
        :filterable="true"
        @change="handleBrandChange"
        clearable
        @clear="handleBrandClear"
      >
        <el-option
          v-for="brand in brandList"
          :key="brand.id"
          :label="brand.name"
          :value="brand.id"
        />
      </el-select>
    </div>

    <!-- 车型选择 -->
    <div v-if="!onlyBrand || onlyModel" class="car-brand-model__model">
      <el-form-item label="车辆车型" v-if="showLabel">
        <el-select
          v-model="modelValue"
          :placeholder="modelPlaceholder"
          :disabled="disabled"
          :filterable="true"
          @change="handleModelChange"
          clearable
          @clear="handleModelClear"
        >
          <el-option
            v-for="model in modelList"
            :key="model.id"
            :label="model.name"
            :value="model.id"
          />
        </el-select>
      </el-form-item>
      <el-select
        v-else
        v-model="modelValue"
        :placeholder="modelPlaceholder"
        :disabled="disabled"
        @change="handleModelChange"
        clearable
        :filterable="true"
        @clear="handleModelClear"
      >
        <el-option
          v-for="model in modelList"
          :key="model.id"
          :label="model.name"
          :value="model.id"
        />
      </el-select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { ElSelect, ElOption, ElFormItem } from 'element-plus'
/**
 * 表单使用示例
 * const formSchema = [
  {
    label: '车辆品牌车型',
    field: 'carInfo',
    component: 'CarBrandModel',
    componentProps: {
      brandApi: getModelListBrandApi,
      modelApi: getModelListApi2,
      brandValue: carData.brandId,
      modelValue: carData.modelId,
      onlyBrand: true,
      on: {
        change: handleChange
      }
    }
  }
]
  * 组件使用方法：
  <CarBrandModel
    :brandApi="fetchBrands"
    :modelApi="fetchModels"
    :brandValue="formData.brandId"
    :modelValue="formData.modelId"
    @change="handleChange"
  />
  */

// 定义组件属性
interface Props {
  // 是否只选择品牌
  onlyBrand?: boolean
  // 是否只选择车型
  onlyModel?: boolean
  // 品牌占位符
  brandPlaceholder?: string
  // 车型占位符
  modelPlaceholder?: string
  // 是否显示标签
  showLabel?: boolean
  // 是否禁用
  disabled?: boolean
  // 品牌API请求函数
  brandApi?: () => Promise<Array<{ id: string | number; name: string }>>
  // 车型API请求函数
  modelApi?: (params?: any) => Promise<Array<{ id: string | number; name: string }>>
  // 品牌默认值
  brandValue?: string | number
  // 车型默认值
  modelValue?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  onlyBrand: false,
  onlyModel: false,
  brandPlaceholder: '请选择车辆品牌',
  modelPlaceholder: '请选择车辆车型',
  showLabel: false,
  disabled: false,
  brandApi: undefined,
  modelApi: undefined,
  brandValue: undefined,
  modelValue: undefined
})

// 定义事件
interface Emits {
  // 品牌变化事件
  (
    e: 'brandChange',
    value: string | number | undefined,
    data: { id: string | number; name: string } | undefined
  ): void
  // 车型变化事件
  (
    e: 'modelChange',
    value: string | number | undefined,
    data: { id: string | number; name: string } | undefined
  ): void
  // 数据变化事件
  (
    e: 'change',
    data: {
      brand: { id: string | number; name: string } | undefined
      model: { id: string | number; name: string } | undefined
    }
  ): void
  // v-model更新事件
  (e: 'update:modelValue', value: string | number | undefined): void
}

const emit = defineEmits<Emits>()

// 定义v-model绑定值
const modelValue = ref<string | number | undefined>(props.modelValue)

// 响应式数据
const brandList = ref<Array<{ id: string | number; name: string }>>([])
const modelList = ref<Array<{ id: string | number; name: string }>>([])
const brandValue = ref<string | number | undefined>(props.brandValue)
const loading = ref(false)

// 计算属性
const currentBrand = computed(() => {
  return brandList.value.find((item) => item.id === brandValue.value)
})

const currentModel = computed(() => {
  return modelList.value.find((item) => item.id === modelValue.value)
})

// 监听属性变化
watch(
  () => props.brandValue,
  (newVal) => {
    brandValue.value = newVal
    if (newVal) {
      fetchModels(newVal)
    }
  }
)

watch(
  () => props.modelValue,
  (newVal) => {
    modelValue.value = newVal
  }
)

// 生命周期
onMounted(async () => {
  // 初始化数据
  if (!props.onlyModel) {
    await fetchBrands()
    // 更新品牌值
    brandValue.value = props.brandValue
    // 如果传入了品牌值，获取对应的车型
    if (props.brandValue) {
      await fetchModels(props.brandValue)
    }
  }
  if (props.onlyModel) {
    await fetchModels()
  }
  // 更新车型值
  modelValue.value = props.modelValue

  console.log('组件初始化后v-model值:', modelValue.value)
})

// 方法
// 获取品牌列表
const fetchBrands = async () => {
  if (props.brandApi) {
    loading.value = true
    try {
      const res = await props.brandApi()
      if (res.code == 0) {
        brandList.value = res.data.map((item) => ({
          id: item.id,
          name: item.name
        }))
        console.log('当前品牌数据', brandList.value)
      } else {
        brandList.value = []
      }
    } catch (error) {
      console.error('获取品牌列表失败:', error)
    } finally {
      loading.value = false
    }
  }
}

// 获取车型列表
const fetchModels = async (brandId?: string | number) => {
  if (props.modelApi) {
    loading.value = true
    try {
      const params = brandId ? { carBrandId: brandId } : undefined
      const res = await props.modelApi(params)
      if (res.code == 0) {
        modelList.value = res.data.map((item) => ({
          id: item.id,
          name: item.name
        }))
        console.log('当前车型数据', modelList.value)
      } else {
        modelList.value = []
      }
    } catch (error) {
      console.error('获取车型列表失败:', error)
    } finally {
      loading.value = false
    }
  }
}

// 品牌变化处理
const handleBrandChange = (value: string | number | undefined) => {
  brandValue.value = value
  // 重置车型
  modelValue.value = undefined
  modelList.value = []
  // 触发车型变化事件
  emit('modelChange', undefined, undefined)
  // 如果选择了品牌，获取对应车型
  if (value && !props.onlyBrand) {
    fetchModels(value)
  }
  // 触发品牌变化事件
  emit('brandChange', value, currentBrand.value)
  // 触发数据变化事件
  emit('change', {
    brand: currentBrand.value,
    model: undefined
  })

  // 更新v-model值
  if (props.onlyBrand) {
    // 如果只选择品牌，返回品牌id
    modelValue.value = value
    emit('update:modelValue', value)
    console.log('品牌变化后v-model值:', value)
  }
}

// 车型变化处理
const handleModelChange = (value: string | number | undefined) => {
  modelValue.value = value
  // 触发车型变化事件
  emit('modelChange', value, currentModel.value)
  // 触发数据变化事件
  emit('change', {
    brand: currentBrand.value,
    model: currentModel.value
  })

  // 更新v-model值并触发事件
  emit('update:modelValue', value)
  console.log('车型变化后v-model值:', value)
}

// 品牌清空处理
const handleBrandClear = () => {
  brandValue.value = undefined
  modelValue.value = undefined
  // 触发品牌变化事件
  emit('brandChange', undefined, undefined)
  // 触发车型变化事件
  emit('modelChange', undefined, undefined)
  // 触发数据变化事件
  emit('change', {
    brand: undefined,
    model: undefined
  })

  // 更新v-model值并触发事件
  emit('update:modelValue', undefined)
  console.log('品牌清空后v-model值:', undefined)
}

// 车型清空处理
const handleModelClear = () => {
  modelValue.value = undefined
  // 触发车型变化事件
  emit('modelChange', undefined, undefined)
  // 触发数据变化事件
  emit('change', {
    brand: currentBrand.value,
    model: undefined
  })

  // 更新v-model值并触发事件
  emit('update:modelValue', undefined)
  console.log('车型清空后v-model值:', undefined)
}

// 重置方法
const reset = () => {
  brandValue.value = undefined
  modelValue.value = undefined
  modelList.value = []
  // 触发品牌变化事件
  emit('brandChange', undefined, undefined)
  // 触发车型变化事件
  emit('modelChange', undefined, undefined)
  // 触发数据变化事件
  emit('change', {
    brand: undefined,
    model: undefined
  })
  // 触发v-model更新事件
  emit('update:modelValue', undefined)
  console.log('组件重置后v-model值:', undefined)
}

// 暴露方法
defineExpose({
  brandList,
  modelList,
  brandValue,
  modelValue,
  currentBrand,
  currentModel,
  fetchBrands,
  fetchModels,
  reset
})
</script>

<style scoped lang="less">
.car-brand-model {
  display: flex;
  align-items: center;
  gap: 20px;

  @media (width <= 768px) {
    flex-direction: column;
    align-items: stretch;

    &__brand,
    &__model {
      width: 100%;
    }
  }

  &__brand,
  &__model {
    flex: 1;
  }
}
</style>
